import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import {
  U21Button,
  U21MenuList,
  U21MenuListOptionProps,
  U21Spacer,
  U21Tooltip,
} from 'app/shared/u21-ui/components';
import {
  IconAlertTriangle,
  IconCheck,
  IconPencil,
  IconTrash,
  IconUsers,
  IconUserShare,
} from '@u21/tabler-icons';

import { DeleteSavedFilterModal } from 'app/modules/filters/components/DeleteSavedFilterModal';
import { EditSavedFilterModal } from 'app/modules/filters/components/EditSavedFilterModal';
import { FilterPinButton } from 'app/modules/filters/components/FilterPinButton';
import { Filter, FilterContext, SavedFilter } from 'app/modules/filters/models';
import { selectAgent } from 'app/modules/session/selectors';
import { useTheme } from '@mui/material/styles';
import pluralize from 'pluralize';

interface Props {
  appliedSavedFilterIds: Set<number>;
  currentFilters: Filter[];
  filterContext: FilterContext;
  onApply: (savedFilter: SavedFilter) => void;
  onDeleted: (savedFilter: SavedFilter) => void;
  onPinnedSavedFilterIdsChange: (ids: number[]) => void;
  onUpdated?: (savedFilter: SavedFilter) => void;
  pinnedSavedFilterIds: number[];
  savedFilters: SavedFilter[];
  staleSavedFilterIds: Set<number>;
}

export const SavedFilterPanel = ({
  appliedSavedFilterIds,
  currentFilters,
  filterContext,
  onApply,
  onDeleted,
  onPinnedSavedFilterIdsChange,
  onUpdated,
  pinnedSavedFilterIds,
  savedFilters,
  staleSavedFilterIds,
}: Props) => {
  const pinnedSet = useMemo(
    () => new Set(pinnedSavedFilterIds),
    [pinnedSavedFilterIds],
  );

  const agent = useSelector(selectAgent);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SavedFilter | undefined>(
    undefined,
  );
  const [editOpen, setEditOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<SavedFilter | undefined>(
    undefined,
  );

  const theme = useTheme();

  const options = useMemo<U21MenuListOptionProps[]>(() => {
    return savedFilters.map((i) => {
      const isOwner = i.created_by_id === agent.id;
      const isApplied = appliedSavedFilterIds.has(i.id);
      const isStale = staleSavedFilterIds.has(i.id);
      const isPinned = pinnedSet.has(i.id);

      const sharedTeamCount = i.shared_team_ids.length;

      const rightIcon = (
        <IconContainer>
          <U21Spacer horizontal useFlexGap>
            {!isOwner && (
              <U21Tooltip tooltip={`Shared by ${i.created_by_name}`}>
                <IconUsers color={theme.palette.text.secondary} />
              </U21Tooltip>
            )}
            {isOwner && sharedTeamCount > 0 && (
              <U21Tooltip
                tooltip={`Shared with ${pluralize('team', sharedTeamCount, true)}`}
              >
                <IconUserShare color={theme.palette.text.secondary} />
              </U21Tooltip>
            )}
            {isApplied && <IconCheck color={theme.palette.primary.main} />}
            {!isApplied && isStale && (
              <U21Tooltip tooltip="Some filters in this saved filter may be unavailable.">
                <IconAlertTriangle color={theme.palette.warning.main} />
              </U21Tooltip>
            )}
            <StyledFilterPinButton
              onPinChange={() => {
                if (isPinned) {
                  onPinnedSavedFilterIdsChange(
                    pinnedSavedFilterIds.filter((id) => id !== i.id),
                  );
                } else {
                  onPinnedSavedFilterIdsChange([...pinnedSavedFilterIds, i.id]);
                }
              }}
              pinned={isPinned}
            />
          </U21Spacer>

          {isOwner && (
            <>
              <ActionButton
                aria-label={`Edit ${i.title}`}
                icon={<IconPencil />}
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingEdit(i);
                  setEditOpen(true);
                }}
              />
              <ActionButton
                aria-label={`Delete ${i.title}`}
                color="error"
                icon={<IconTrash />}
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingDelete(i);
                  setDeleteOpen(true);
                }}
              />
            </>
          )}
        </IconContainer>
      );

      return {
        description: i.description ?? undefined,
        onClick: () => onApply(i),
        rightIcon,
        text: i.title,
        typographyProps: { ellipsis: true },
      };
    });
  }, [
    agent.id,
    appliedSavedFilterIds,
    onPinnedSavedFilterIdsChange,
    pinnedSavedFilterIds,
    pinnedSet,
    staleSavedFilterIds,
    savedFilters,
    onApply,
    theme,
  ]);

  return (
    <>
      <U21MenuList options={options} placeholder="Search saved filters" />
      {pendingDelete && (
        <DeleteSavedFilterModal
          filterContext={filterContext}
          onClose={() => setDeleteOpen(false)}
          onDeleted={onDeleted}
          open={deleteOpen}
          savedFilter={pendingDelete}
        />
      )}
      {pendingEdit && (
        <EditSavedFilterModal
          filters={currentFilters}
          onClose={() => setEditOpen(false)}
          onUpdated={onUpdated}
          open={editOpen}
          savedFilter={pendingEdit}
        />
      )}
    </>
  );
};

const IconContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledFilterPinButton = styled(FilterPinButton)`
  margin: 0 -8px;
`;

const ActionButton = styled(U21Button)`
  max-width: 0;
  overflow: hidden;
  padding-left: 0;
  padding-right: 0;
  transition: ${(props) => props.theme.transitions.create(['all'])};

  // reveal on row hover or when the button itself receives keyboard focus
  li:hover &,
  &:focus-visible {
    margin-right: -8px; // -8px cancels out button padding
    margin-left: 0;
    max-width: 36px; // 36px is the button width
    padding-left: 8px;
    padding-right: 8px;
  }
`;
